import { supabase } from '../lib/supabase';

export const chatService = {
  // Create or get existing thread
  async createThread({ listingId, seekerId, entrepreneurId }) {
    try {
      // First check if thread already exists
      const { data: existingThread, error: checkError } = await supabase
        .from('threads')
        .select('*')
        .eq('listing_id', listingId)
        .eq('seeker_id', seekerId)
        .single();

      if (existingThread) {
        return existingThread;
      }

      // Create new thread if it doesn't exist
      const { data, error } = await supabase
        .from('threads')
        .insert({
          listing_id: listingId,
          seeker_id: seekerId,
          entrepreneur_id: entrepreneurId
        })
        .select(`
          *,
          listings!listing_id (
            *
          ),
          seeker_profile:public_profiles!seeker_id (
            display_name
          ),
          entrepreneur_profile:public_profiles!entrepreneur_id (
            display_name
          )
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create thread error:', error);
      throw error;
    }
  },

  // Get user's threads
  async getUserThreads(userId, limit = 20, offset = 0) {
    try {
      const { data, error, count } = await supabase
        .from('threads')
        .select(`
          *,
          listings!listing_id (
            *
          ),
          seeker_profile:public_profiles!seeker_id (
            id,
            display_name,
            bio,
            location,
            skills,
            experience,
            qualifications,
            certifications,
            website,
            created_at
          ),
          entrepreneur_profile:public_profiles!entrepreneur_id (
            id,
            display_name,
            bio,
            location,
            skills,
            experience,
            qualifications,
            certifications,
            website,
            created_at
          )
        `, { count: 'exact' })
        .or(`seeker_id.eq.${userId},entrepreneur_id.eq.${userId}`)
        .range(offset, offset + limit - 1)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      return { threads: data, count };
    } catch (error) {
      console.error('Get user threads error:', error);
      throw error;
    }
  },

  // Get thread with messages
  async getThreadWithMessages(threadId, messageLimit = 50, messageOffset = 0) {
    try {
      // Get thread details
      const { data: thread, error: threadError } = await supabase
        .from('threads')
        .select(`
          *,
          listings!listing_id (
            *
          ),
          seeker_profile:public_profiles!seeker_id (
            id,
            display_name,
            bio,
            location,
            skills,
            experience,
            qualifications,
            certifications,
            website,
            created_at
          ),
          entrepreneur_profile:public_profiles!entrepreneur_id (
            id,
            display_name,
            bio,
            location,
            skills,
            experience,
            qualifications,
            certifications,
            website,
            created_at
          )
        `)
        .eq('id', threadId)
        .single();

      if (threadError) throw threadError;

      // Get messages for the thread
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select(`
          *,
          profiles:public_profiles!sender_id (
            display_name
          )
        `)
        .eq('thread_id', threadId)
        .range(messageOffset, messageOffset + messageLimit - 1)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      return {
        thread,
        messages
      };
    } catch (error) {
      console.error('Get thread with messages error:', error);
      throw error;
    }
  },

  // Send a message
  async sendMessage({ threadId, senderId, body }) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          thread_id: threadId,
          sender_id: senderId,
          body
        })
        .select(`
          *,
          profiles:public_profiles!sender_id (
            display_name
          )
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  },

  // Mark messages as read
  async markMessagesAsRead(threadId, userId) {
    try {
      console.log(`📖 Marking messages as read for thread ${threadId}, user ${userId}`);
      
      const { data, error, count } = await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('thread_id', threadId)
        .neq('sender_id', userId)
        .is('read_at', null)
        .select();

      if (error) {
        console.error('❌ Mark messages as read error:', error);
        throw error;
      }
      
      console.log(`✅ Marked ${data?.length || 0} messages as read`);
      return data;
    } catch (error) {
      console.error('❌ Mark messages as read error:', error);
      throw error;
    }
  },

  // Get unread message count for user
  async getUnreadCount(userId) {
    try {
      // Get all threads for the user
      const { data: threads, error: threadsError } = await supabase
        .from('threads')
        .select('id')
        .or(`seeker_id.eq.${userId},entrepreneur_id.eq.${userId}`);

      if (threadsError) throw threadsError;

      const threadIds = threads.map(thread => thread.id);

      if (threadIds.length === 0) {
        return 0;
      }

      // Count unread messages in those threads (not sent by the user)
      const { count, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .in('thread_id', threadIds)
        .neq('sender_id', userId)
        .is('read_at', null);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Get unread count error:', error);
      throw error;
    }
  },

  // Get unread message count for a specific thread
  async getThreadUnreadCount(threadId, userId) {
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .eq('thread_id', threadId)
        .neq('sender_id', userId)
        .is('read_at', null);

      if (error) return { data: 0, error };
      return { data: count || 0, error: null };
    } catch (error) {
      console.error('Get thread unread count error:', error);
      return { data: 0, error };
    }
  },

  // Subscribe to new messages in a thread
  subscribeToThread(threadId, callback) {
    return supabase
      .channel(`thread-${threadId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `thread_id=eq.${threadId}`
        },
        callback
      )
      .subscribe();
  },

  // Subscribe to thread updates (for user's threads list)
  subscribeToUserThreads(userId, callback) {
    return supabase
      .channel(`user-threads-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'threads',
          filter: `seeker_id=eq.${userId}`
        },
        callback
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'threads',
          filter: `entrepreneur_id=eq.${userId}`
        },
        callback
      )
      .subscribe();
  },

  // Unsubscribe from channel
  unsubscribe(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
};